import React, { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import ReactiveRender, { isMobileView } from '../../ReactiveRender';
import AccountSettingsMarkingCriteriaDeskt from '../AccountSettingsMarkingCriteriaDeskt';

import MarkingCriteriaCard from '../MarkingCriteriaCard';
import {
  deleteMarkingCriteria,
  createNewMarkingCriteria,
  updateMarkingCriteriaPreferences,
} from '../../../service.js';
import Loader from '../../Loader';
import MarkingMethodologyDialog from '../../CreateNewMarkingCriteria/SelectMarkingMethodologyDialog';
import Toast from '../../Toast/index.js';
import { toast } from 'react-toastify';
import {
  useMarkingCriterias,
  useMarkingCriteriasWithPreferences,
} from '../../state/hooks.js';
import { isNullOrEmpty } from '../../../utils/arrays.js';
import { buildClonePayload } from '../../../utils/subjects';
import { useApiSubjects } from '../../../hooks/useSubjects';

export default function AccountSettingsRoot(props) {
  const queryClient = useQueryClient();
  const { apiSubjects } = useApiSubjects();
  const [openMarkingMethodologyDialog, setOpenMarkingMethodologyDialog] =
    useState(false);
  const [selectedCriteria, setSelectedCriteria] = useState(new Set());
  const [showPreferences, setShowPreferences] = useState(false);
  const [showHidden, setShowHidden] = useState(false);
  const [showOnlyMineFilter, setShowOnlyMineFilter] = useState(false);
  const {
    data: markingCriterias,
    isLoadingdata: isLoadingMarkingCriterias,
    setData: setMarkingCriterias,
    resetData: resetMarkingCriterias,
  } = useMarkingCriterias();

  const {
    data: markingCriteriaswithPreferences,
    isLoadingdata: isLoadingMarkingCriteriaswithPreferences,
    setData: setMarkingCriteriaswithPreferences,
    resetData: resetMarkingCriteriaswithPreferences,
  } = useMarkingCriteriasWithPreferences();

  const hasNoData = !markingCriterias && !markingCriteriaswithPreferences;

  const preferencesMap = isNullOrEmpty(markingCriteriaswithPreferences)
    ? new Map()
    : new Map(
        markingCriteriaswithPreferences.map((pref) => [
          pref.id,
          {
            id: pref.id,
            teacherId: pref.teacherId,
            markingCriteriaId: pref.id,
            isVisible: pref.isVisible,
            sortOrder: pref.sortOrder,
            visible: pref.visible,
          },
        ])
      );

  const hiddenCriteriaIds = new Set(
    Array.from(preferencesMap.values())
      .filter((pref) => pref.isVisible === false)
      .map((pref) => pref.markingCriteriaId)
  );

  const userPreferences = isNullOrEmpty(markingCriteriaswithPreferences)
    ? {}
    : markingCriteriaswithPreferences.reduce((acc, pref) => {
        acc[pref.id] = {
          visible: pref.visible !== false && pref.isVisible !== false,
          sortOrder: pref.sortOrder || 999,
          id: pref.id,
          teacherId: pref.teacherId,
          markingCriteriaId: pref.id,
          isVisible: pref.isVisible,
        };
        return acc;
      }, {});

  const allMarkingCriterias = (markingCriterias || []).filter(
    (criteria) => criteria?.type === 'RUBRICS'
  );
  const filteredMarkingCriterias = isNullOrEmpty(markingCriterias)
    ? []
    : markingCriterias.filter(
        (criteria) => !hiddenCriteriaIds.has(criteria.id)
      );

  // Apply hidden and mine filters
  let currentDataset = allMarkingCriterias;

  if (showHidden) {
    currentDataset = currentDataset.filter((criteria) =>
      hiddenCriteriaIds.has(criteria.id)
    );
  } else {
    currentDataset = currentDataset.filter(
      (criteria) => !hiddenCriteriaIds.has(criteria.id)
    );
  }

  if (showOnlyMineFilter) {
    currentDataset = currentDataset.filter((criteria) => !criteria.isSystem);
  }
  const currentLoading =
    isLoadingMarkingCriterias || isLoadingMarkingCriteriaswithPreferences;
  const isLoading = false;
  const showLoader = hasNoData && currentLoading;

  const createMarkingCriteria = (markingCriteria) => {
    const createdMarkingCriteria = buildClonePayload(
      markingCriteria,
      apiSubjects,
      {
        type: 'RUBRICS',
        criterias: markingCriteria.criterias,
      }
    );

    createNewMarkingCriteria(createdMarkingCriteria)
      .then(async (res) => {
        const newTemplateId = res?.id?.value ?? res?.id;
        if (!newTemplateId) {
          toast(<Toast message={'Error cloning marking template'} />);
          return;
        }

        const originalTemplateIndex = filteredCriterias.findIndex(
          (criteria) => criteria.id === markingCriteria.id
        );
        if (originalTemplateIndex !== -1) {
          const originalSortOrder =
            userPreferences[markingCriteria.id]?.sortOrder ?? 999;
          const newSortOrder = originalSortOrder + 1;

          try {
            await updateMarkingCriteriaPreferences('REORDER', {
              toolIds: [newTemplateId],
              newSortOrder,
            });
          } catch (error) {
            console.error(
              'Error setting sort order for duplicated template:',
              error
            );
          }
        }
        queryClient.invalidateQueries({ queryKey: ['markingCriterias'] });
        queryClient.invalidateQueries({
          queryKey: ['markingCriteriasWithPreferences'],
        });
        queryClient.invalidateQueries({
          queryKey: ['feedbackTool', 'items', 'markingCriteria'],
        });
        queryClient.invalidateQueries({
          queryKey: ['feedbackTool', 'itemsCount', 'markingCriteria'],
        });
        toast(
          <Toast
            message={'Copied marking template'}
            link={markingCriteriaUrl(newTemplateId)}
          />
        );
        resetAllMarkingCriterias();
      })
      .catch(() => toast(<Toast message={'Error cloning marking template'} />));
  };

  const handleSelectCriteria = (criteriaId) => {
    const newSelected = new Set(selectedCriteria);
    newSelected.has(criteriaId)
      ? newSelected.delete(criteriaId)
      : newSelected.add(criteriaId);
    setSelectedCriteria(newSelected);
  };

  const handleSelectAll = () => {
    if (currentDataset.length === 0) return;

    const allIds = currentDataset.map((c) => c.id);
    const isAllSelected =
      selectedCriteria.size === allIds.length &&
      allIds.every((id) => selectedCriteria.has(id));

    setSelectedCriteria(isAllSelected ? new Set() : new Set(allIds));
  };

  const resetAllMarkingCriterias = () => {
    resetMarkingCriterias();
    resetMarkingCriteriaswithPreferences();
  };

  const handleFilterSystemOnes = () => {
    // This function is no longer needed as filtering is handled internally
  };

  const handleToggleDataset = () => {
    setShowPreferences((prev) => !prev);
    setSelectedCriteria(new Set());
  };

  const handleReorderMarkingCriteria = async (reorderedItems) => {
    try {
      setTimeout(() => {
        resetAllMarkingCriterias();
      }, 500);
    } catch (error) {
      console.error('Error handling reorder:', error);
    }
  };

  // NOTE: previously this short-circuited the whole tree with the branded
  // <Loader/> while marking-criteria data was in flight. Loader uses
  // position:fixed (full-viewport overlay), so the user perceived three
  // load phases — app-boot loader, then page chrome appearing for a moment
  // (Suspense released + route mounted), then this loader flashing back on
  // top of the chrome before data resolved. Now: skip the gate so the page
  // chrome (sidebar, breadcrumbs, tabs, action buttons) stays visible while
  // the list area populates. allMarkingCriterias defaults to [] when
  // markingCriterias is still undefined, so the rest of the render is
  // already safe for the no-data state.
  // if (showLoader) return <Loader />;

  let filteredCriterias = currentDataset || [];

  filteredCriterias.sort((a, b) => {
    const aOrder = userPreferences[a.id]?.sortOrder ?? 999;
    const bOrder = userPreferences[b.id]?.sortOrder ?? 999;
    return aOrder - bOrder;
  });

  const filteredAndSortedMarkingCriterias = filteredCriterias;

  return (
    <>
      <AccountSettingsMarkingCriteriaDeskt
        {...{
          filteredAndSortedMarkingCriterias,
          resetMarkingCriterias: resetAllMarkingCriterias,
          handleFilterSystemOnes,
          selectedCriteria,
          setSelectedCriteria,
          handleSelectAll,
          showPreferences,
          handleToggleDataset,
          currentDatasetName: 'Visible Items',
          allMarkingCriteria: filteredCriterias,
          userPreferences,
          isLoading,
          onReorderMarkingCriteria: handleReorderMarkingCriteria,
          updateMarkingCriteriaPreferences: updateMarkingCriteriaPreferences,
          isOnlyMineFilter: showOnlyMineFilter,
          isSystemFilterApplied: showOnlyMineFilter,
          filteredCriteriasCount: filteredCriterias.length,
          showHidden,
          setShowHidden,
          showOnlyMineFilter,
          setShowOnlyMineFilter,
          createMarkingCriteria,
          handleSelectCriteria,
        }}
      />
      {openMarkingMethodologyDialog && (
        <MarkingMethodologyDialog
          setOpenMarkingMethodologyDialog={setOpenMarkingMethodologyDialog}
        />
      )}
    </>
  );
}

function markingCriteriaUrl(id) {
  return `#/tools/rubrics/${id}`;
}
